<template>
  <div style="display: none" />
</template>

<script>
import L from 'leaflet'
import 'maplibre-gl/dist/maplibre-gl.css'
import { markRaw } from 'vue'
import { findRealParent } from 'vue2-leaflet'
import { loadMaplibre, hasWebglSupport } from '@/global/components/map/maplibreLoader'

// MapLibre GL renders vector tiles onto a canvas overlay that Leaflet keeps in sync with its own
// pan/zoom - the same idea googlemutant uses for Google tiles (see Vue2LeafletGoogleMutant).
// maplibre-gl and its Leaflet binding are loaded lazily so the GL bundle is only fetched once a
// user actually switches to a vector map type.

// How long a lost WebGL context is given to come back before the map gives up on it and lets the
// parent draw the raster fallback instead. Chrome restores a context that was dropped for GPU
// pressure within a frame or two; one that never comes back is the tab's context budget being
// spent elsewhere, and no amount of waiting fixes it.
const CONTEXT_RESTORE_GRACE_MS = 3000

// The patched layer class, built once the plugin has registered L.MaplibreGL.
let ThrottledMaplibreGL = null

// Whether MapLibre's shared worker pool has been sized and built up front - see prepareWorkers.
let workersPrewarmed = false

// Upper bound on the tile workers we ask for. Each one is another copy of MapLibre's worker bundle
// held for the life of the page, and past a handful the tiles stop being the bottleneck anyway.
const MAX_TILE_WORKERS = 4

/**
 * maplibre-gl-leaflet drives the GL map from Leaflet's zoom animation, and its handlers assume
 * every animation runs to completion. Zooming faster than the 250ms animation breaks that
 * assumption in three ways, and all three are repaired here:
 *
 *  - `_transitionEnd` queues a rAF per animation. Interrupted animations stack them up, so a
 *    frame's worth of `_resizeContainer` + `jumpTo` runs once per interrupted zoom.
 *  - each of those also arms its own `once('moveend')` -> `_zoomEnd` -> `_update` -> `jumpTo`,
 *    and a jumpTo fires the moveend that triggers the next armed handler. N interrupted zooms
 *    collapse into a synchronous chain of N full style updates and tile-coverage recalculations.
 *  - a queued `_zoomEnd` landing in the middle of the *next* animation clears `_zooming` and
 *    transforms the canvas to a position Leaflet has already moved on from, which is what leaves
 *    the map sitting there as a grey rectangle.
 *
 * On top of that, maplibre nulls `map.style` when the WebGL context is lost (see Map#_contextLost)
 * while the Leaflet bridge keeps calling `jumpTo` on every mouse move. Those calls throw from
 * inside Leaflet's event dispatch, which aborts the dispatch mid-way and leaves Leaflet stuck in
 * `_animatingZoom` - the map freezes and stays frozen. `_canDraw` is what keeps the bridge quiet
 * until the context is back.
 *
 * @returns {object} Leaflet layer class
 */
function throttledMaplibreLayerClass () {
  if (ThrottledMaplibreGL) {
    return ThrottledMaplibreGL
  }

  const base = L.MaplibreGL.prototype

  ThrottledMaplibreGL = L.MaplibreGL.extend({
    /**
     * True only while there is a GL map with a live style behind it. Every override below leans on
     * this rather than on `_glMap` alone: the layer keeps its `_glMap` through a context loss, it
     * is the style underneath that goes away.
     *
     * @returns {boolean}
     */
    _canDraw () {
      return !!(this._map && this._glMap && !this._glLost && this._glMap.style)
    },

    /**
     * One pending frame, not one per interrupted animation.
     */
    _transitionEnd () {
      if (this._transitionFrame !== null && this._transitionFrame !== undefined) {
        L.Util.cancelAnimFrame(this._transitionFrame)
      }

      this._transitionFrame = L.Util.requestAnimFrame(this._applyTransitionEnd, this)
    },

    _applyTransitionEnd () {
      this._transitionFrame = null

      if (!this._canDraw()) {
        return
      }

      const map = this._map
      const center = map.getCenter()
      const zoom = map.getZoom()
      const offset = map.latLngToContainerPoint(map.getBounds().getNorthWest())

      this._resizeContainer()
      L.DomUtil.setTransform(this._glMap._actualCanvas, offset, 1)

      // Re-armed rather than stacked - see the note above about moveend chaining.
      if (!this._onGlMoveEnd) {
        this._onGlMoveEnd = L.Util.bind(this._zoomEnd, this)
      }

      this._glMap.off('moveend', this._onGlMoveEnd)
      this._glMap.once('moveend', this._onGlMoveEnd)

      this._recordTransform(center, zoom - 1)
      this._glMap.jumpTo({ center, zoom: zoom - 1 })
    },

    /**
     * Same work as upstream, but it refuses to finish a zoom that Leaflet has already replaced with
     * a newer one - that zoom's own `zoomend` will do it, with the right scale.
     */
    _zoomEnd () {
      if (!this._canDraw() || this._map._animatingZoom) {
        return
      }

      L.DomUtil.setTransform(this._glMap._actualCanvas, null, this._map.getZoomScale(this._map.getZoom()))
      this._zooming = false
      this._update()
    },

    _update (event) {
      if (!this._canDraw()) {
        return
      }

      return base._update.call(this, event)
    },

    /**
     * Upstream's `_pinchZoom` is the same jumpTo as `_transformGL`, written out again. Routing it
     * through the one place means it goes through the de-duplication below too.
     */
    _pinchZoom () {
      if (!this._canDraw()) {
        return
      }

      this._transformGL(this._glMap)
    },

    /**
     * jumpTo is not a cheap call: MapLibre recalculates every layer in the style for the new zoom
     * and recomputes tile coverage for the whole viewport. A single discrete zoom used to make
     * three of them - Leaflet fires `zoom`, then `zoomend`, then the layer's own transition-end
     * handler runs, and all three ask for the same position. Two of the three were redundant.
     *
     * Recording what the GL map was last told means the repeats collapse. `_applyTransitionEnd`
     * records its own jump for the same reason. Worst case a record goes stale and we jump when we
     * did not have to - it can never skip a jump that was needed, because a stale record cannot
     * match the position being asked for.
     *
     * @param {object} gl
     */
    _transformGL (gl) {
      const center = this._map.getCenter()
      const zoom = this._map.getZoom() - 1

      if (this._isAtTransform(center, zoom)) {
        return
      }

      this._recordTransform(center, zoom)
      gl.jumpTo({ center: [center.lng, center.lat], zoom })
    },

    /**
     * @param {object} center Leaflet LatLng
     * @param {number} zoom MapLibre zoom, i.e. Leaflet's minus one
     * @returns {boolean}
     */
    _isAtTransform (center, zoom) {
      const applied = this._appliedTransform

      return !!applied && applied.zoom === zoom && applied.lat === center.lat && applied.lng === center.lng
    },

    /**
     * @param {object} center Leaflet LatLng
     * @param {number} zoom MapLibre zoom, i.e. Leaflet's minus one
     */
    _recordTransform (center, zoom) {
      this._appliedTransform = { lat: center.lat, lng: center.lng, zoom }
    },

    _animateZoom (event) {
      if (!this._canDraw()) {
        return
      }

      return base._animateZoom.call(this, event)
    },

    onRemove (map) {
      if (this._transitionFrame !== null && this._transitionFrame !== undefined) {
        L.Util.cancelAnimFrame(this._transitionFrame)
        this._transitionFrame = null
      }

      if (this._glMap && this._onGlMoveEnd) {
        this._glMap.off('moveend', this._onGlMoveEnd)
      }

      this._onGlMoveEnd = null

      return base.onRemove.call(this, map)
    }
  })

  return ThrottledMaplibreGL
}

export default {
  name: 'Vue2LeafletMaplibre',

  props: {
    // URL of a MapLibre style JSON (style spec v8), or an inline style object.
    // Cannot be named "style", Vue 2 reserves that attribute.
    styleUrl: {
      type: [String, Object],
      required: true
    },
    options: {
      type: Object,
      default: () => ({})
    },
    attribution: {
      type: String,
      default: ''
    },
    visible: {
      type: Boolean,
      default: true
    }
  },

  created () {
    // Plain instance properties on purpose - nothing renders from them. `paintRepairs` caps the
    // self-repair so a genuinely broken style can never turn into a reload loop,
    // `paintCheckPending` keeps the check itself off the pan/zoom path (see checkPaintOnce), and
    // `styleShielded` keeps the shielding to once per loaded style (see shieldStyle).
    this.paintRepairs = 0
    this.paintCheckPending = false
    this.styleShielded = false
    this.contextRestoreTimeout = null
  },

  async mounted () {
    // Asked first, because it is a plain canvas probe that needs none of what follows. Without a
    // WebGL context MapLibre throws inside Leaflet's onAdd, which leaves the layer half-registered:
    // _glMap is never assigned, but Leaflet's zoom handlers are, so every subsequent zoom throws on
    // _glMap._actualCanvas. Bailing out here means the parent hears about it immediately, and a
    // browser that cannot draw the map never downloads the bundle for it either.
    if (!hasWebglSupport()) {
      this.$emit('unsupported')

      return
    }

    // Started before the bundle is awaited, not after. Handing MapLibre a URL means it cannot begin
    // fetching the style until it exists, which puts the style round trip end to end after the
    // bundle rather than alongside it. The warm-up below usually means there is no bundle left to
    // wait for, but the ordering costs nothing and still holds when it has not finished.
    const stylePromise = this.fetchStyle()

    // Usually already loaded and evaluated by the time this runs: MapTileLayers warms the same
    // promise while the view config request is still in flight (see maplibreLoader).
    const [maplibreModule] = await loadMaplibre()

    // Whatever the parallel fetch came back with, or the URL again so MapLibre fetches it the way
    // it always did - by this point the promise has almost always already settled.
    const style = await stylePromise

    // Destroyed while the chunk or the style was still loading.
    if (this._isDestroyed) {
      return
    }

    this.prepareWorkers(maplibreModule)

    this.mapObject = new (throttledMaplibreLayerClass())({
      style: style || this.styleUrl,
      attribution: this.attribution,
      // Vector tiles overzoom past their native max zoom, so there is no maxNativeZoom to clamp to.
      maxZoom: this.options.maxZoom,
      // Passed straight through to the MapLibre map (see MaplibreGL#_initGL). The cross-fade
      // repaints the whole viewport on every frame for 300ms after each tile arrives, and on a
      // slow connection tiles keep arriving - which is the stutter while the map loads. Nothing
      // is lost by dropping it: Leaflet is already animating the canvas as a whole.
      fadeDuration: 0,
      // We serve these tiles ourselves and they do not change under a running session; re-fetching
      // them the moment a Cache-Control max-age lapses only buys a burst of requests mid-pan.
      refreshExpiredTiles: false,
      // How much bigger than the viewport the GL canvas is drawn, per axis. The plugin's 0.1 makes
      // it 1.2x wide and 1.2x tall - 1.44x the pixels, so 44% more tiles on every load and every
      // pan, and these tiles are 187KB gzipped apiece at the zoom the app opens at.
      //
      // The margin does not do what it looks like it does. Its apparent job is covering the gap
      // when a zoom-out shrinks the canvas, but Leaflet's zoom step scales by 0.5 and 1.2 * 0.5 is
      // still only 0.6 of the viewport - the gap is there either way. What it genuinely covers is
      // the lag while a pan waits on the 32ms update throttle, and that is worth perhaps 64px at a
      // fast drag. 0.05 is ~77px on a 1544px viewport: still covers the pan, 16% fewer tiles.
      padding: 0.05,
      // Validating a style walks every layer, paint and layout property in it against the style
      // spec, and ours are 112 layers each - all of it on the main thread, between the style
      // arriving and the first tile request going out. These styles are not user input: they come
      // from our own tile server and VectorStyleController has already refused anything that is
      // not a style document before it ever reaches the browser.
      validateStyle: false
    })

    this.shieldFromVue(this.mapObject)

    L.DomEvent.on(this.mapObject, this.$listeners)

    this.parentContainer = findRealParent(this.$parent)
    if (this.parentContainer) {
      this.shieldFromVue(this.parentContainer.mapObject)
      this.parentContainer.addLayer(this, !this.visible)
    }

    const glMap = this.mapObject.getMaplibreMap?.()
    if (glMap) {
      this.shieldFromVue(glMap)
      this.watchContextLoss(glMap)

      // A style that is being replaced has to be shielded again once it is up; until then the
      // flags from the previous one are the ones in force.
      glMap.on('styledataloading', () => {
        this.styleShielded = false
      })
      glMap.on('styledata', () => this.shieldLoadedStyle(glMap))
      this.shieldLoadedStyle(glMap)

      // Last line of defence. The cause of the paint corruption is fixed at its source, but any
      // future reactive reference into the Leaflet map would bring it back, and it fails silently -
      // a map in the wrong colours, with nothing in the console. Detect it and rebuild the style.
      //
      // `idle` fires after every pan and every zoom, and the check walks the whole style
      // (OSM 3D is ~150 layers) - so it runs on the first idle after a style loads and not
      // again until the next one. Corruption comes from the style graph being rewritten as it is
      // built, so it is there from the first frame or not at all; watching every idle for it only
      // bought continuous allocation and GC on a live tracking map.
      glMap.on('idle', () => this.checkPaintOnce(glMap))
    }
  },

  beforeDestroy () {
    if (this.contextRestoreTimeout) {
      clearTimeout(this.contextRestoreTimeout)
      this.contextRestoreTimeout = null
    }

    if (this.parentContainer && this.mapObject) {
      this.parentContainer.removeLayer(this)
    }
    this.mapObject = null
  },

  methods: {
    /**
     * A lost WebGL context is not an error MapLibre reports upwards - it drops its style, keeps the
     * map object, and waits. Leaflet keeps driving that map, so the bridge has to be told to hold
     * off (see _canDraw) until the browser hands the context back.
     *
     * A context that does not come back leaves a grey canvas and nothing else, so past the grace
     * period the parent is told the same thing it is told when there was never a context to begin
     * with, and draws the raster fallback.
     *
     * @param {object} glMap
     */
    watchContextLoss (glMap) {
      glMap.on('webglcontextlost', () => {
        if (this.mapObject) {
          this.mapObject._glLost = true
        }

        this.contextRestoreTimeout = setTimeout(() => {
          this.contextRestoreTimeout = null

          if (this.mapObject?._glLost) {
            this.$emit('unsupported')
          }
        }, CONTEXT_RESTORE_GRACE_MS)
      })

      glMap.on('webglcontextrestored', () => {
        if (this.contextRestoreTimeout) {
          clearTimeout(this.contextRestoreTimeout)
          this.contextRestoreTimeout = null
        }

        if (!this.mapObject) {
          return
        }

        this.mapObject._glLost = false
        // MapLibre reloads the style it was holding, so it has to be shielded again, and the
        // canvas has to be put back where Leaflet thinks it is.
        this.styleShielded = false
        this.mapObject._update()
      })
    },

    /**
     * The layer ids of a loaded style, without serialising it.
     *
     * `getStyle()` is `style.serialize()` - a deep clone of every layer, source and sprite entry in
     * the document. Both the shielding and the corruption check used to start from one, which put
     * a full clone of a ~150 layer style on the main thread every time the style fired an event,
     * including each sprite and image update during the initial load.
     *
     * @param {object} glMap
     * @returns {string[]}
     */
    layerIds (glMap) {
      if (!glMap.style) {
        return []
      }

      try {
        return glMap.getLayersOrder()
      }
      catch (error) {
        return []
      }
    },

    /**
     * Two fill layers that declare different colours must not evaluate to the same colour object.
     * When they do, Vue has collapsed the paint state and every fill renders in one colour.
     *
     * @param {object} glMap
     * @returns {boolean}
     */
    isPaintCorrupted (glMap) {
      const seen = new Map()

      for (const id of this.layerIds(glMap)) {
        const layer = glMap.getLayer(id)

        if (layer?.type !== 'fill') {
          continue
        }

        const declared = layer.getPaintProperty?.('fill-color')
        const evaluated = layer.paint?.get?.('fill-color')?.value?.value

        if (typeof declared !== 'string' || !evaluated) {
          continue
        }

        for (const [otherDeclared, otherEvaluated] of seen) {
          if (otherDeclared !== declared && otherEvaluated === evaluated) {
            return true
          }
        }

        seen.set(declared, evaluated)
      }

      return false
    },

    /**
     * Runs the corruption check at most once per loaded style - see the `idle` handler for why it
     * cannot sit on every idle.
     *
     * @param {object} glMap
     */
    checkPaintOnce (glMap) {
      if (!this.paintCheckPending) {
        return
      }

      this.paintCheckPending = false
      this.repairPaintIfCorrupted(glMap)
    },

    /**
     * The shielding above is meant to make this unreachable, so a repair here is not routine
     * self-healing - it means a reference path into MapLibre got past markRaw and is worth
     * chasing down. Warn rather than repair silently.
     *
     * @param {object} glMap
     */
    repairPaintIfCorrupted (glMap) {
      if (this.paintRepairs >= 3 || !this.isPaintCorrupted(glMap)) {
        return
      }

      this.paintRepairs += 1

      console.warn(
        '[Vue2LeafletMaplibre] Evaluated paint corrupted - rebuilding style ' +
        `(repair ${this.paintRepairs}/3, style: ${typeof this.styleUrl === 'string' ? this.styleUrl : 'inline'}). ` +
        'Something reactive is reaching into the MapLibre style graph; check for Leaflet or ' +
        'MapLibre references held in a component data() or store state.'
      )

      glMap.setStyle(this.styleUrl, { diff: false })
    },

    /**
     * Vue 2 deep-observes anything it can reach from reactive state, and MapLibre is reachable
     * from the Leaflet map through map._layers. Once Vue rewrites MapLibre's internals as reactive
     * getters, every fill layer ends up sharing a single evaluated colour object - the whole map
     * renders in one colour, whichever layer was evaluated last.
     *
     * markRaw sets __v_skip, which is what observe() actually tests, so flagging the objects Vue
     * must not touch is a reliable guard no matter which reference path Vue takes to get there.
     * Do not swap this back for a bare _isVue flag: that was the check in Vue 2.6, but 2.7 replaced
     * it with __v_skip and a flag Vue no longer reads protects nothing.
     *
     * @param {object} target
     */
    shieldFromVue (target) {
      if (target) {
        markRaw(target)
      }
    },

    /**
     * Flags a class prototype instead of an instance. MapLibre rebuilds a layer's evaluated paint
     * container on every recalculate, so a flag set on one instance is gone by the next frame -
     * but Vue reads __v_skip through the prototype chain, so flagging the prototype covers every
     * instance the class will ever produce.
     *
     * @param {object} value
     */
    shieldPrototypeOf (value) {
      if (!value) {
        return
      }

      const prototype = Object.getPrototypeOf(value)

      if (prototype && prototype !== Object.prototype) {
        markRaw(prototype)
      }
    },

    /**
     * Shields a style the first time it is reported loaded, and not again.
     *
     * `styledata` is not a style-loaded event: it also fires for every sprite and image update, and
     * again from Style#update whenever anything in the document changed. Re-shielding on each of
     * them walked the whole style over and over while the map was still loading its first tiles.
     * Once is enough, because the flags go on classes rather than on instances - see shieldStyle.
     *
     * @param {object} glMap
     */
    shieldLoadedStyle (glMap) {
      if (this.styleShielded || !glMap.style || !glMap.isStyleLoaded?.()) {
        return
      }

      this.styleShielded = true
      this.shieldStyle(glMap)
      // A freshly loaded style is a fresh chance for the corruption to appear, so each one earns
      // exactly one check.
      this.paintCheckPending = true
    },

    /**
     * Shields the style graph. The layers, their evaluated paint containers and the colour objects
     * inside them are what actually get corrupted, and all three are rebuilt as the map runs, so
     * the flags go on their classes rather than on the objects themselves.
     *
     * @param {object} glMap
     */
    shieldStyle (glMap) {
      const style = glMap.style

      if (!style) {
        return
      }

      this.shieldFromVue(style)
      this.shieldPrototypeOf(style)

      for (const id of this.layerIds(glMap)) {
        const layer = glMap.getLayer(id)

        if (!layer) {
          continue
        }

        this.shieldPrototypeOf(layer)
        this.shieldPrototypeOf(layer.paint)
        this.shieldPrototypeOf(layer.layout)

        // Colour objects live one level deeper, behind a paint property whose name differs per
        // layer type, so take whichever this layer happens to expose.
        const colorProperty = ['fill-color', 'line-color', 'background-color', 'text-color']
          .map(name => {
            try {
              return layer.paint?.get?.(name)?.value?.value
            }
            catch (error) {
              return null
            }
          })
          .find(value => value && typeof value === 'object')

        this.shieldPrototypeOf(colorProperty)
      }
    },

    /**
     * Sizes and builds MapLibre's tile worker pool, once for the page and before the first map.
     *
     * MapLibre GL v5 runs **one** tile worker on every browser but Safari (WorkerPool.workerCount),
     * so a viewport's worth of tiles is decoded and laid out one after another. That is what the
     * map looks like while it waits: the raster relief in these styles needs no worker and paints
     * immediately, so the first thing on screen is a shaded relief with not one vector layer on it.
     * The tiles are not the problem - they arrive gzipped in ~250ms - it is that nothing can start
     * on the second one until the first is done.
     *
     * Half the logical cores, which is the same shape as MapLibre's own Safari default, capped so a
     * many-core machine does not hold more worker bundles than the tiles can keep busy.
     *
     * prewarm() then builds the pool up front and, unlike the lazy default, keeps it past the last
     * map. This app destroys and rebuilds maps constantly - switching map type remounts this
     * component by key, and every module with a map mounts its own - so that pool was being paid
     * for over and over. Both calls have to happen before any map exists to have any effect.
     *
     * @param {object} maplibreModule the dynamically imported module
     */
    prepareWorkers (maplibreModule) {
      if (workersPrewarmed) {
        return
      }

      workersPrewarmed = true

      // maplibre-gl ships as a UMD bundle with a single module.exports, so webpack hands the whole
      // library back under `default` - reading these off the namespace itself finds nothing.
      const maplibregl = maplibreModule?.default || maplibreModule

      if (!maplibregl) {
        return
      }

      const cores = navigator.hardwareConcurrency || 4

      maplibregl.setWorkerCount?.(Math.max(Math.min(Math.floor(cores / 2), MAX_TILE_WORKERS), 1))
      maplibregl.prewarm?.()
    },

    /**
     * The style document, fetched alongside the GL bundle rather than after it.
     *
     * Every failure here falls back to null, which leaves MapLibre to fetch the URL itself exactly
     * as before - this is a head start, not a new dependency, and it must not be able to break a
     * map that would otherwise have loaded. The response is same-origin and the route sits behind
     * the session cookie, same as the fetch MapLibre would have made.
     *
     * Handing MapLibre the document instead of the URL is only safe because these styles carry no
     * relative references - sprite, glyphs and tiles are all absolute URLs (see
     * VectorStyleController). A style with relative paths would lose its base URL this way, and
     * every icon and label with it.
     *
     * @returns {Promise<Object|null>} the style document, or null to let MapLibre fetch it
     */
    async fetchStyle () {
      // Already an inline style object - there is nothing to fetch.
      if (typeof this.styleUrl !== 'string') {
        return this.styleUrl
      }

      try {
        const response = await fetch(this.styleUrl, { credentials: 'same-origin' })

        return response.ok ? await response.json() : null
      }
      catch (error) {
        return null
      }
    }
  }
}
</script>
